import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Church, Loader2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset successful. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF9F5] p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-xl bg-[#2D5A27] flex items-center justify-center text-white">
            <Church className="h-6 w-6" />
          </div>
          <span className="text-2xl font-serif font-semibold">FlockCare</span>
        </div>
        <h2 className="font-serif text-3xl font-bold">Choose a new password</h2>
        <p className="mt-2 text-sm text-[#7A8A78]">Enter a new password for your account.</p>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="password">New password</Label>
            <Input id="password" type="password" data-testid="reset-password-input" value={password}
              onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white" minLength={6} required />
          </div>
          {!token && <p className="text-sm text-destructive">Missing reset token. Use the link from your email.</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" disabled={loading || !token} data-testid="reset-submit-button"
            className="h-11 w-full bg-[#2D5A27] hover:bg-[#23471F] text-white font-semibold">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
          </Button>
          <Link to="/login" className="flex items-center justify-center gap-2 text-sm font-medium text-[#2D5A27] hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </form>
      </div>
    </div>
  );
}
